import { Fragment, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Fade from '@mui/material/Fade'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import type { Contract as ContractType } from '@models/Contract'
import { formatDate } from '@util/date-util'

import { PropertyRow } from '../../../theme/components/PropertyRow'
import { formatAutoEventConfig } from '../util/contractinstellingen-utils'

export { Contract }

type Props = {
  expanded?: boolean
  contract: ContractType
}

const Contract = ({ contract, expanded = false }: Props) => {
  const [isExpanded, setIsExpanded] = useState(expanded)

  const {
    combi,
    coordAannemer,
    gebiedscode,
    gebiedscodeLabel,
    soloNetbeheerders,
    netbeheerders,
    autoEventConfigs,
  } = contract
  const micConfigs = autoEventConfigs.filter(
    ({ isCombiUitvoering }) => isCombiUitvoering,
  )
  const nonMicConfigs = autoEventConfigs.filter(
    ({ isCombiUitvoering }) => !isCombiUitvoering,
  )

  return (
    <Accordion
      key={combi + gebiedscode}
      onChange={(_, expanded) => setIsExpanded(expanded)}
      expanded={isExpanded}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Fade in={!isExpanded} appear={false} unmountOnExit>
          <Typography>{gebiedscodeLabel}</Typography>
        </Fade>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={4}>
          <Grid size={{ xs: 12 }}>
            <PropertyRow label="Gebied" value={gebiedscodeLabel} variant="h5" />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <PropertyRow label="Combi" value={combi} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <PropertyRow label="Gebiedscode" value={gebiedscode} />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <PropertyRow
              label="Netbeheerders"
              value={netbeheerders.join(', ')}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <PropertyRow
              label="Coordinerende aannemers"
              value={coordAannemer.join(', ')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <PropertyRow
              label="Solo netbeheerders"
              value={soloNetbeheerders?.map(
                ({ netbeheerder, ingangsDatum }, index) => (
                  <Fragment key={`${netbeheerder}-${ingangsDatum}`}>
                    {`${netbeheerder} vanaf ${formatDate(ingangsDatum)}`}
                    {index < soloNetbeheerders.length - 1 && <br />}
                  </Fragment>
                ),
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <PropertyRow
              label="Automatisch mee in combi"
              value={
                micConfigs.length > 0
                  ? micConfigs
                      .map(formatAutoEventConfig)
                      .map((configString, index) => (
                        <Fragment key={configString}>
                          {configString}
                          {index < micConfigs.length - 1 && <br />}
                        </Fragment>
                      ))
                  : undefined
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <PropertyRow
              label="Automatisch uit combi"
              value={
                nonMicConfigs.length > 0
                  ? nonMicConfigs
                      .map(formatAutoEventConfig)
                      .map((configString, index) => (
                        <Fragment key={configString}>
                          {configString}
                          {index < nonMicConfigs.length - 1 && <br />}
                        </Fragment>
                      ))
                  : undefined
              }
            />
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}
